/**
 * Graphics quality - how many real lights the hall runs.
 *
 * The renderer is forward-rendered WebGL: every visible light is evaluated
 * for every lit pixel, so the light budget IS the graphics setting. Two
 * tiers:
 *
 *   low   the classroom baseline the whole project is tuned against -
 *         roughly ten visible lights, integrated-graphics safe.
 *   high  the fuller ceiling: extra downlights under the luminaire rows,
 *         roughly twenty visible, for machines that can keep 60 fps.
 *
 * The default choice is AUTO, which does what games call scalability:
 * measure this machine, then decide. It starts at the lean tier (never
 * punish a slow machine to find out it is slow), promotes to high after a
 * few seconds of comfortable frame times, and demotes - permanently, for
 * the session - if high turns out not to hold. Demote-and-lock rather than
 * oscillate: a setting that flickers between looks reads as a bug, and the
 * student it happens to is the one with the weakest machine.
 *
 * App-level like language and lighting: the CHOICE is remembered per
 * machine; the measurement is redone each session because hardware and
 * browser both change under us.
 */

export type QualityChoice = 'auto' | 'high' | 'low'
export type QualityTier = 'high' | 'low'

const STORAGE_KEY = 'simfab.quality'

function readStored(): QualityChoice {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v === 'auto' || v === 'high' || v === 'low') return v
  } catch {
    // Storage blocked - auto is the right default anyway.
  }
  return 'auto'
}

let choice: QualityChoice = readStored()

// ---------------------------------------------------------------- governor

/** Frames per measurement window (~1 s at 60 fps). */
const WINDOW = 60
/**
 * Sustained fps that earns the next rung up.
 *
 * Was 55, set when a promotion bought the extra lights and the whole image
 * pipeline in one go: a single irreversible bet, so it wanted a wide
 * margin. The ladder changed that. A promotion now buys one rung, the
 * cheapest available first, and a rung that does not hold is given back
 * within two windows with everything below it kept - so attempting is
 * cheap and reversible, and the bar can sit where a machine that is
 * comfortably interactive still gets offered something.
 *
 * It matters that this is not set too high. The hall grew a good deal of
 * geometry, and a machine sitting at 54 fps was being denied a rung that
 * costs half a millisecond - and, above it, the ambient occlusion that is
 * the single biggest difference between this room looking rendered and
 * looking photographed.
 */
const PROMOTE_FPS = 50
/** Fps below which high has visibly failed. */
const DEMOTE_FPS = 45
/** Good windows in a row before promoting. */
const PROMOTE_WINDOWS = 3
/** Bad windows in a row before demoting. */
const DEMOTE_WINDOWS = 2
/**
 * Sub-threshold windows in a row before concluding the machine is simply
 * lean. Generous on purpose: the first seconds of a session are shader
 * compiles and chunk loads, and a fast machine must not be locked lean by
 * its own warm-up.
 */
const GIVE_UP_WINDOWS = 6

/**
 * THE LADDER, and why there is one instead of two tiers.
 *
 *   0  lean lights, no image pipeline - the classroom baseline
 *   1  the fuller ceiling
 *   2  the fuller ceiling AND ambient occlusion, bloom, SMAA
 *
 * These used to be one flag: a machine got the extra lights and the image
 * pipeline together, or neither. Measured on the integrated GPU this
 * project is tuned against, they are nothing like the same purchase - the
 * extra lights cost about 0.5 ms a frame, the pipeline about 3.0 ms, six
 * times as much. Bundled, a machine that could comfortably afford the
 * lights and not the pipeline was offered both, failed, and was demoted to
 * neither - losing a half-millisecond effect to pay for a three-millisecond
 * one it had already given up.
 *
 * So the ladder is climbed and descended one rung at a time, cheapest
 * first, and a rung that has been measured and failed is never offered
 * again for the rest of the session - which is the same no-oscillation
 * promise the old demote-and-lock made, just kept per rung instead of for
 * the whole staircase.
 */
const MAX_LEVEL = 2
let level = 0
/**
 * The highest rung still worth trying. Starts at the top; a demotion pulls
 * it down to below whatever just failed, and it never rises again.
 */
let ceiling = MAX_LEVEL
let goodStreak = 0
let badStreak = 0
let failStreak = 0
let frames = 0
let elapsed = 0

const listeners = new Set<() => void>()

function notify(): void {
  for (const fn of listeners) fn()
}

export function currentQuality(): QualityChoice {
  return choice
}

/** How many real lights the hall runs, whatever the choice says. */
export function resolvedTier(): QualityTier {
  return choice === 'auto' ? (level >= 1 ? 'high' : 'low') : choice
}

/**
 * Whether the image pipeline (AO, bloom, SMAA) draws.
 *
 * The top rung of the ladder, and separate from the light budget because it
 * costs six times as much - see the ladder note above. An explicit choice
 * still means what it says: `high` is everything, `low` is nothing.
 */
export function resolvedImage(): boolean {
  return choice === 'auto' ? level >= 2 : choice === 'high'
}

export function setQuality(next: QualityChoice): void {
  if (next === choice) return
  choice = next
  try {
    localStorage.setItem(STORAGE_KEY, next)
  } catch {
    // Not remembered, still applied.
  }
  if (next === 'auto') {
    // A fresh verdict: the machine may have been busy last time it was
    // measured, and re-measuring costs nothing.
    level = 0
    ceiling = MAX_LEVEL
    goodStreak = 0
    badStreak = 0
    failStreak = 0
    frames = 0
    elapsed = 0
  }
  notify()
}

export function onQualityChange(fn: () => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

/**
 * Feed one frame's delta (seconds). Called from the render loop; does
 * nothing unless the choice is auto and the verdict could still change.
 */
export function qualityFrame(dt: number): void {
  if (choice !== 'auto') return
  // Nothing left to learn only at the BOTTOM of the ladder with nothing
  // above it left to try. Anywhere else the window still has to be measured,
  // because a rung that was affordable when it was taken may stop being
  // affordable - a heavier view, a busier machine - and standing on a rung
  // is exactly when demotion has to be able to fire. Guarding this with
  // `level >= ceiling` instead made the top rung permanent: the pipeline
  // stayed on through 25 s at 41 fps, six under the demote bar.
  if (level === 0 && ceiling === 0) return

  // A huge delta is a hidden tab or a chunk load, not the GPU - throw the
  // window away rather than let it count against the machine.
  if (dt > 0.25) {
    frames = 0
    elapsed = 0
    return
  }

  frames++
  elapsed += dt
  if (frames < WINDOW) return

  const fps = frames / elapsed
  frames = 0
  elapsed = 0

  // Falling over on the rung we are standing on: step down, and write off
  // this rung and everything above it for the rest of the session.
  if (level > 0 && fps < DEMOTE_FPS) {
    badStreak++
    goodStreak = 0
    if (badStreak >= DEMOTE_WINDOWS) {
      ceiling = level - 1
      level = ceiling
      badStreak = 0
      notify()
    }
    return
  }
  badStreak = 0

  if (fps >= PROMOTE_FPS) {
    goodStreak++
    failStreak = 0
    if (goodStreak >= PROMOTE_WINDOWS) {
      level = Math.min(level + 1, ceiling)
      goodStreak = 0
      notify()
    }
    return
  }

  // Comfortable enough to stay, not comfortable enough to climb.
  goodStreak = 0
  failStreak++
  // A machine that cannot hold 55 where it stands, well past warm-up, has
  // answered the question; stop asking and leave it here.
  if (failStreak >= GIVE_UP_WINDOWS) {
    ceiling = level
    notify()
  }
}
